/**
 * MakeMyBobblehead configurator
 * Self-contained frontend foundation for the development theme.
 * AI generation and Shopify product linkage are intentionally incomplete until backend/product exist.
 */

const STEP_COUNT = 5;
const BASE_SIZE_ID = '7';
const PHOTO_MAX_EDGE = 2048;
const PHOTO_JPEG_QUALITY = 0.92;

/**
 * Re-encode the customer photo to a standard RGB JPEG via canvas.
 * Fixes iPhone HEIC/HEIF (when the browser can decode it), odd MIME aliases,
 * huge camera dimensions, and other mobile formats OpenAI rejects.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
async function normalizePhotoForGeneration(file) {
  if (!(file instanceof File) && !(file instanceof Blob)) {
    throw Object.assign(new Error('Please upload a photo to generate your bobblehead.'), {
      code: 'MISSING_PHOTO',
    });
  }

  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || 'photo').toLowerCase();
  const looksHeic =
    type.includes('heic') || type.includes('heif') || /\.heic$|\.heif$/i.test(name);

  let bitmap = null;
  try {
    if (typeof createImageBitmap === 'function') {
      bitmap = await createImageBitmap(file);
    }
  } catch {
    bitmap = null;
  }

  if (!bitmap) {
    bitmap = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(
          Object.assign(
            new Error(
              looksHeic
                ? 'This iPhone HEIC photo could not be converted here. Please export it as JPG or PNG and try again.'
                : 'We could not read that photo. Please upload a JPG, PNG or WebP image.'
            ),
            { code: looksHeic ? 'UNSUPPORTED_HEIC' : 'INVALID_FILE_TYPE' }
          )
        );
      };
      img.src = url;
    });
  }

  const srcW = bitmap.width || bitmap.naturalWidth || 0;
  const srcH = bitmap.height || bitmap.naturalHeight || 0;
  if (!srcW || !srcH) {
    if (typeof bitmap.close === 'function') bitmap.close();
    throw Object.assign(new Error('We could not read that photo. Please try another image.'), {
      code: 'INVALID_FILE_TYPE',
    });
  }

  const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    if (typeof bitmap.close === 'function') bitmap.close();
    throw Object.assign(new Error('Your browser could not prepare that photo. Please try JPG or PNG.'), {
      code: 'INVALID_FILE_TYPE',
    });
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  if (typeof bitmap.close === 'function') bitmap.close();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (!result) {
          reject(
            Object.assign(new Error('We could not prepare that photo for generation. Please try JPG or PNG.'), {
              code: 'INVALID_FILE_TYPE',
            })
          );
          return;
        }
        resolve(result);
      },
      'image/jpeg',
      PHOTO_JPEG_QUALITY
    );
  });

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Calls the secure generation backend (OpenAI key never leaves the server).
 *
 * @param {BobbleheadState} state
 * @param {object} config
 * @returns {Promise<{ok: boolean, status: string, code?: string, message?: string, previewUrl?: string, customizationId?: string}>}
 */
export async function generateBobblehead(state, config = {}) {
  const endpoint = String(config.apiEndpoint || '').trim();
  if (!endpoint) {
    return {
      ok: false,
      status: 'ERROR',
      code: 'NOT_CONNECTED',
      message:
        'Preview generation is not connected yet. Set the generation API URL in the configurator section settings (development theme).',
    };
  }

  if (!(state.photo instanceof File)) {
    return {
      ok: false,
      status: 'ERROR',
      code: 'MISSING_PHOTO',
      message: 'Please upload a photo to generate your bobblehead.',
    };
  }

  let uploadFile;
  try {
    uploadFile = await normalizePhotoForGeneration(state.photo);
  } catch (error) {
    return {
      ok: false,
      status: 'ERROR',
      code: error?.code || 'INVALID_FILE_TYPE',
      message:
        error?.message ||
        'We could not prepare that photo for generation. Please upload a JPG, PNG or WebP image.',
    };
  }

  const maxBytes = config.maxUploadBytes || 10 * 1024 * 1024;
  if (uploadFile.size > maxBytes) {
    return {
      ok: false,
      status: 'ERROR',
      code: 'FILE_TOO_LARGE',
      message: 'That image is too large. Please use a file under 10 MB.',
    };
  }

  const formData = new FormData();
  formData.append('photo', uploadFile, uploadFile.name || 'photo.jpg');
  formData.append('size', String(state.size || ''));
  formData.append('baseShape', String(state.baseShape || ''));
  formData.append('baseColour', String(state.baseColour || ''));
  formData.append('subject', String(state.subject || 'anyone'));
  if (state.customizationId) {
    formData.append('customizationId', String(state.customizationId));
  }

  let response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
  } catch {
    return {
      ok: false,
      status: 'ERROR',
      code: 'NETWORK_ERROR',
      message:
        'Could not reach the generation studio. Check that the API is running and the API URL is correct.',
    };
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || !payload?.ok) {
    return {
      ok: false,
      status: 'ERROR',
      code: payload?.code || 'GENERATION_FAILED',
      message:
        payload?.message ||
        'We could not generate your bobblehead preview right now. Please try again in a moment.',
    };
  }

  if (!payload.previewUrl || typeof payload.previewUrl !== 'string') {
    return {
      ok: false,
      status: 'ERROR',
      code: 'GENERATION_EMPTY',
      message: 'Generation completed without an image. Please try again.',
    };
  }

  // Only accept persisted HTTPS preview URLs (never data URLs).
  if (!/^https:\/\//i.test(payload.previewUrl)) {
    return {
      ok: false,
      status: 'ERROR',
      code: 'PERSIST_REQUIRED',
      message:
        'Your bobblehead preview could not be saved securely. Please try generating again.',
    };
  }

  return {
    ok: true,
    status: 'READY',
    previewUrl: payload.previewUrl,
    customizationId: payload.customizationId,
  };
}

/**
 * @typedef {object} BobbleheadState
 * @property {File|null} photo
 * @property {string|null} photoPreviewUrl
 * @property {string} subject
 * @property {string} size
 * @property {number|null} variantId
 * @property {string} baseShape
 * @property {string} baseColour
 * @property {string} baseText
 * @property {number} price
 * @property {string|null} customizationId
 * @property {string|null} generatedPreviewUrl
 * @property {'EMPTY'|'UPLOADING'|'PROCESSING'|'GENERATING'|'READY'|'ERROR'} status
 * @property {number} step
 */

function createInitialState(config) {
  const baseSize = config.sizes.find((s) => s.id === BASE_SIZE_ID) || config.sizes[0];
  const baseColour = config.baseColours[0];
  const baseShape = config.baseShapes[0];

  return /** @type {BobbleheadState} */ ({
    photo: null,
    photoPreviewUrl: null,
    subject: (config.subjects && config.subjects[0]?.id) || 'anyone',
    size: baseSize.id,
    variantId: null,
    baseShape: baseShape.id,
    baseColour: baseColour.id,
    baseText: '',
    price: baseSize.displayPrice,
    customizationId: null,
    generatedPreviewUrl: null,
    status: 'EMPTY',
    step: 1,
  });
}

function formatMoney(cents, currency = 'GBP') {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  } catch {
    return `£${(cents / 100).toFixed(2)}`;
  }
}

function priceDeltaLabel(sizeId, sizes, currency) {
  const base = sizes.find((s) => s.id === BASE_SIZE_ID)?.displayPrice ?? 0;
  const current = sizes.find((s) => s.id === sizeId)?.displayPrice ?? 0;
  if (current <= base) return 'Starting size';
  return `+${formatMoney(current - base, currency)} vs 7″`;
}

/** Temporary client-side ID only — not authoritative. */
function createTemporaryCustomizationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tmp_${crypto.randomUUID()}`;
  }
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Estimated progress stages — not real OpenAI processing progress. */
const GENERATION_STAGES = [
  { at: 0, label: 'Preparing your photo…' },
  { at: 5, label: 'Preparing your photo…' },
  { at: 20, label: 'Setting up your bobblehead…' },
  { at: 40, label: 'Creating your bobblehead…' },
  { at: 65, label: 'Refining the details…' },
  { at: 85, label: 'Finishing your preview…' },
  { at: 95, label: 'Finalising your preview…' },
];

/** Typical end-to-end estimate used only for UX timing (seconds). */
const GENERATION_ESTIMATE_SECONDS = 55;

function resolveVariantId(config, sizeId) {
  if (!config.product?.variants?.length) return null;

  const sizeMeta = config.sizes.find((s) => s.id === sizeId);
  const candidates = [sizeMeta?.label, `${sizeId}"`, sizeId, `${sizeId}″`].filter(Boolean);

  for (const variant of config.product.variants) {
    const haystack = `${variant.title || ''} ${variant.option1 || ''}`.toLowerCase();
    for (const candidate of candidates) {
      if (haystack.includes(String(candidate).toLowerCase().replace('″', '"'))) {
        return variant.id;
      }
    }
  }
  return null;
}

function resolveDisplayPrice(config, sizeId, variantId) {
  if (variantId && config.product?.variants) {
    const variant = config.product.variants.find((v) => v.id === variantId);
    if (variant && typeof variant.price === 'number') return variant.price;
  }
  return config.displayPrices[sizeId] ?? config.sizes.find((s) => s.id === sizeId)?.displayPrice ?? 3999;
}

function cartAddEndpoint(config) {
  const root = window.Shopify?.routes?.root;
  if (typeof root === 'string' && root.length) {
    const normalized = root.endsWith('/') ? root : `${root}/`;
    return `${normalized}cart/add.js`;
  }
  if (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) {
    return Theme.routes.cart_add_url;
  }
  return config.cartAddUrl || '/cart/add.js';
}

class BobbleheadConfigurator {
  /**
   * @param {HTMLElement} root
   * @param {object} config
   */
  constructor(root, config) {
    this.root = root;
    this.config = config;
    this.state = createInitialState(config);
    this.state.variantId = resolveVariantId(config, this.state.size);
    this.state.price = resolveDisplayPrice(config, this.state.size, this.state.variantId);
    /** @type {Promise<void>|null} */
    this.generateInFlight = null;
    this.cartSubmitInFlight = false;
    /** @type {number|null} */
    this.progressTimer = null;
    this.progressStartedAt = 0;
    this.estimatedProgress = 0;

    this.els = {
      progressItems: [...root.querySelectorAll('[data-progress-step]')],
      panels: [...root.querySelectorAll('[data-step-panel]')],
      globalStatus: root.querySelector('[data-global-status]'),
      dropzone: root.querySelector('[data-dropzone]'),
      photoInput: root.querySelector('[data-photo-input]'),
      dropzoneEmpty: root.querySelector('[data-dropzone-empty]'),
      dropzonePreview: root.querySelector('[data-dropzone-preview]'),
      photoPreview: root.querySelector('[data-photo-preview]'),
      photoError: root.querySelector('[data-photo-error]'),
      photoReplace: root.querySelector('[data-photo-replace]'),
      photoRemove: root.querySelector('[data-photo-remove]'),
      sizeOptions: root.querySelector('[data-size-options]'),
      shapeOptions: root.querySelector('[data-shape-options]'),
      colourOptions: root.querySelector('[data-colour-options]'),
      subjectOptions: root.querySelector('[data-subject-options]'),
      baseText: root.querySelector('[data-base-text]'),
      baseTextCount: root.querySelector('[data-base-text-count]'),
      summarySize: root.querySelector('[data-summary-size]'),
      summaryShape: root.querySelector('[data-summary-shape]'),
      summaryColour: root.querySelector('[data-summary-colour]'),
      summaryText: root.querySelector('[data-summary-text]'),
      summaryPrice: root.querySelector('[data-summary-price]'),
      summaryPhoto: root.querySelector('[data-summary-photo]'),
      readyPanel: root.querySelector('[data-ready-panel]'),
      reviewGenerate: root.querySelector('[data-review-generate]'),
      readySize: root.querySelector('[data-ready-size]'),
      readyShape: root.querySelector('[data-ready-shape]'),
      readyColour: root.querySelector('[data-ready-colour]'),
      readyText: root.querySelector('[data-ready-text]'),
      readyPrice: root.querySelector('[data-ready-price]'),
      back: root.querySelector('[data-action-back]'),
      next: root.querySelector('[data-action-next]'),
      generate: root.querySelector('[data-action-generate]'),
      addToCart: root.querySelector('[data-action-add-to-cart]'),
      edit: root.querySelector('[data-action-edit]'),
      atcLabel: root.querySelector('[data-atc-label]'),
      previewStage: root.querySelector('[data-preview-stage]'),
      previewPlaceholder: root.querySelector('[data-preview-placeholder]'),
      generationProgress: root.querySelector('[data-generation-progress]'),
      progressTrack: root.querySelector('[data-progress-track]'),
      progressFill: root.querySelector('[data-progress-fill]'),
      progressPercent: root.querySelector('[data-progress-percent]'),
      progressStage: root.querySelector('[data-progress-stage]'),
      progressEta: root.querySelector('[data-progress-eta]'),
      sourceRef: root.querySelector('[data-source-ref]'),
      stagePhoto: root.querySelector('[data-stage-photo]'),
      previewResult: root.querySelector('[data-preview-result]'),
      previewComposite: root.querySelector('[data-preview-composite]'),
      generatedPreview: root.querySelector('[data-generated-preview]'),
      baseOverlay: root.querySelector('[data-base-overlay]'),
      baseOverlayText: root.querySelector('[data-base-overlay-text]'),
      aiDisclaimer: root.querySelector('[data-ai-disclaimer]'),
      aiAckWrap: root.querySelector('[data-ai-ack-wrap]'),
      aiAck: root.querySelector('[data-ai-ack]'),
      atcValidation: root.querySelector('[data-atc-validation]'),
      previewStateLabel: root.querySelector('[data-preview-state-label]'),
      previewSizeLabel: root.querySelector('[data-preview-size-label]'),
      previewPrice: root.querySelector('[data-preview-price]'),
      generationNotice: root.querySelector('[data-generation-notice]'),
    };

    this.#renderOptionControls();
    this.#bindEvents();
    this.#render();
  }

  #bindEvents() {
    const { dropzone, photoInput, photoReplace, photoRemove, baseText, back, next, generate, addToCart, edit } =
      this.els;

    photoInput?.addEventListener('change', () => {
      const file = photoInput.files?.[0];
      if (file) this.#handlePhotoFile(file);
    });

    dropzone?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        photoInput?.click();
      }
    });

    ;['dragenter', 'dragover'].forEach((type) => {
      dropzone?.addEventListener(type, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });

    ;['dragleave', 'drop'].forEach((type) => {
      dropzone?.addEventListener(type, (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-dragover');
      });
    });

    dropzone?.addEventListener('drop', (event) => {
      const file = event.dataTransfer?.files?.[0];
      if (file) this.#handlePhotoFile(file);
    });

    photoReplace?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      photoInput?.click();
    });

    photoRemove?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#clearPhoto();
    });

    baseText?.addEventListener('input', () => {
      const max = this.config.maxBaseTextLength || 15;
      let value = baseText.value.slice(0, max);
      if (value !== baseText.value) baseText.value = value;
      this.state.baseText = value;
      // Base text is composited by us — do not invalidate the AI preview.
      this.#updateBaseOverlay();
      this.#updateSummary();
      this.#updateCharCount();
      this.#updateActions();
    });

    back?.addEventListener('click', () => this.#goToStep(this.state.step - 1));
    next?.addEventListener('click', () => this.#onNext());
    generate?.addEventListener('click', () => this.#onGenerate());
    addToCart?.addEventListener('click', () => this.#onAddToCart());
    edit?.addEventListener('click', () => this.#goToStep(1));
    this.els.aiAck?.addEventListener('change', () => {
      if (this.els.aiAck.checked) this.#setAtcValidation('');
      this.#updateActions();
    });
  }

  #renderOptionControls() {
    const { sizes, baseShapes, baseColours, subjects } = this.config;
    const currency = this.config.currency || 'GBP';

    if (this.els.subjectOptions && subjects?.length) {
      this.els.subjectOptions.innerHTML = subjects
        .map((subject) => {
          const checked = subject.id === this.state.subject;
          return `
            <button
              type="button"
              class="bobblehead-configurator__shape-card"
              role="radio"
              aria-checked="${checked}"
              data-subject-id="${subject.id}"
            >
              <span>${subject.label}</span>
            </button>
          `;
        })
        .join('');

      this.els.subjectOptions.querySelectorAll('[data-subject-id]').forEach((button) => {
        button.addEventListener('click', () => this.#selectSubject(button.getAttribute('data-subject-id')));
      });
    }

    if (this.els.sizeOptions) {
      this.els.sizeOptions.innerHTML = sizes
        .map((size) => {
          const checked = size.id === this.state.size;
          return `
            <button
              type="button"
              class="bobblehead-configurator__option-card"
              role="radio"
              aria-checked="${checked}"
              data-size-id="${size.id}"
            >
              <span class="bobblehead-configurator__option-size">${size.label}</span>
              <span class="bobblehead-configurator__option-price">${formatMoney(size.displayPrice, currency)}</span>
              <span class="bobblehead-configurator__option-delta">${priceDeltaLabel(size.id, sizes, currency)}</span>
            </button>
          `;
        })
        .join('');

      this.els.sizeOptions.querySelectorAll('[data-size-id]').forEach((button) => {
        button.addEventListener('click', () => this.#selectSize(button.getAttribute('data-size-id')));
      });
    }

    if (this.els.shapeOptions) {
      this.els.shapeOptions.innerHTML = baseShapes
        .map((shape) => {
          const checked = shape.id === this.state.baseShape;
          return `
            <button
              type="button"
              class="bobblehead-configurator__shape-card"
              role="radio"
              aria-checked="${checked}"
              data-shape-id="${shape.id}"
            >
              <span class="bobblehead-configurator__shape-visual bobblehead-configurator__shape-visual--${shape.id}" aria-hidden="true"></span>
              <span>${shape.label}</span>
            </button>
          `;
        })
        .join('');

      this.els.shapeOptions.querySelectorAll('[data-shape-id]').forEach((button) => {
        button.addEventListener('click', () => this.#selectShape(button.getAttribute('data-shape-id')));
      });
    }

    if (this.els.colourOptions) {
      this.els.colourOptions.innerHTML = baseColours
        .map((colour) => {
          const checked = colour.id === this.state.baseColour;
          return `
            <button
              type="button"
              class="bobblehead-configurator__swatch"
              role="radio"
              aria-checked="${checked}"
              aria-label="${colour.label}"
              data-colour-id="${colour.id}"
              title="${colour.label}"
            >
              <span class="bobblehead-configurator__swatch-chip" style="--swatch-colour: ${colour.hex}" aria-hidden="true"></span>
              <span class="bobblehead-configurator__swatch-label">${colour.label}</span>
            </button>
          `;
        })
        .join('');

      this.els.colourOptions.querySelectorAll('[data-colour-id]').forEach((button) => {
        button.addEventListener('click', () => this.#selectColour(button.getAttribute('data-colour-id')));
      });
    }
  }

  #selectSubject(subjectId) {
    if (!subjectId) return;
    this.state.subject = subjectId;
    this.#invalidateGeneratedPreview();
    this.#syncSelected(this.els.subjectOptions, '[data-subject-id]', subjectId, 'data-subject-id');
    this.#updateActions();
  }

  #selectSize(sizeId) {
    if (!sizeId) return;
    this.state.size = sizeId;
    this.state.variantId = resolveVariantId(this.config, sizeId);
    this.state.price = resolveDisplayPrice(this.config, sizeId, this.state.variantId);
    this.#invalidateGeneratedPreview();
    this.#syncSelected(this.els.sizeOptions, '[data-size-id]', sizeId, 'data-size-id');
    this.#updatePreviewChrome();
    this.#updateSummary();
    this.#updateActions();
  }

  #selectShape(shapeId) {
    if (!shapeId) return;
    this.state.baseShape = shapeId;
    this.#invalidateGeneratedPreview();
    this.#syncSelected(this.els.shapeOptions, '[data-shape-id]', shapeId, 'data-shape-id');
    this.#updatePreviewChrome();
    this.#updateSummary();
    this.#updateActions();
  }

  #selectColour(colourId) {
    if (!colourId) return;
    this.state.baseColour = colourId;
    this.#invalidateGeneratedPreview();
    this.#syncSelected(this.els.colourOptions, '[data-colour-id]', colourId, 'data-colour-id');
    this.#updatePreviewChrome();
    this.#updateSummary();
    this.#updateActions();
  }

  #invalidateGeneratedPreview() {
    if (this.state.status === 'READY' || this.state.generatedPreviewUrl) {
      this.state.status = 'EMPTY';
      this.state.generatedPreviewUrl = null;
      this.els.previewResult?.toggleAttribute('hidden', true);
      this.#updatePreviewStatus();
    }
  }

  #syncSelected(container, selector, value, attr) {
    container?.querySelectorAll(selector).forEach((el) => {
      el.setAttribute('aria-checked', String(el.getAttribute(attr) === value));
    });
  }

  #handlePhotoFile(file) {
    this.#setPhotoError('');

    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '');
    const accepted = this.config.acceptedMimeTypes || [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];
    const looksHeic =
      type.includes('heic') || type.includes('heif') || /\.heic$|\.heif$/i.test(name);
    const typeOk =
      !file.type ||
      accepted.includes(file.type) ||
      accepted.includes(type) ||
      type === 'image/jpg' ||
      type === 'image/pjpeg' ||
      looksHeic ||
      /\.(jpe?g|png|webp|heic|heif)$/i.test(name);

    if (!typeOk) {
      this.#setPhotoError('Please upload a JPG, PNG or WebP image.');
      return;
    }

    if (file.size > (this.config.maxUploadBytes || 10 * 1024 * 1024)) {
      this.#setPhotoError('That image is too large. Please use a file under 10 MB.');
      return;
    }

    if (this.state.photoPreviewUrl) URL.revokeObjectURL(this.state.photoPreviewUrl);

    this.state.status = 'UPLOADING';
    this.#updatePreviewStatus();

    const previewUrl = URL.createObjectURL(file);
    this.state.photo = file;
    this.state.photoPreviewUrl = previewUrl;
    this.state.generatedPreviewUrl = null;
    this.state.status = 'EMPTY';
    if (!this.state.customizationId) {
      this.state.customizationId = createTemporaryCustomizationId();
    }

    if (this.els.photoPreview) this.els.photoPreview.src = previewUrl;
    if (this.els.summaryPhoto) {
      this.els.summaryPhoto.src = previewUrl;
      this.els.summaryPhoto.hidden = false;
    }
    if (this.els.stagePhoto) {
      this.els.stagePhoto.src = previewUrl;
    }
    this.els.sourceRef?.toggleAttribute('hidden', false);

    this.els.dropzoneEmpty?.toggleAttribute('hidden', true);
    this.els.dropzonePreview?.toggleAttribute('hidden', false);

    if (looksHeic) {
      this.#setPhotoError(
        'iPhone HEIC photo detected. We’ll convert it to JPG when you generate. If generation fails, export the photo as JPG and try again.'
      );
    }

    this.#updatePreviewStatus();
    this.#updateActions();
  }

  #clearPhoto() {
    if (this.state.photoPreviewUrl) URL.revokeObjectURL(this.state.photoPreviewUrl);
    this.state.photo = null;
    this.state.photoPreviewUrl = null;
    this.state.generatedPreviewUrl = null;
    this.state.status = 'EMPTY';
    if (this.els.photoInput) this.els.photoInput.value = '';
    if (this.els.stagePhoto) this.els.stagePhoto.removeAttribute('src');
    if (this.els.summaryPhoto) {
      this.els.summaryPhoto.removeAttribute('src');
      this.els.summaryPhoto.hidden = true;
    }
    this.els.sourceRef?.toggleAttribute('hidden', true);
    this.els.dropzoneEmpty?.toggleAttribute('hidden', false);
    this.els.dropzonePreview?.toggleAttribute('hidden', true);
    this.els.previewResult?.toggleAttribute('hidden', true);
    this.#setPhotoError('');
    this.#updatePreviewStatus();
    this.#updateActions();
  }

  #setPhotoError(message) {
    const el = this.els.photoError;
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = message;
  }

  #setAtcValidation(message, tone = 'error') {
    const el = this.els.atcValidation;
    const ackError = Boolean(message) && tone === 'error' && this.els.aiAck && !this.els.aiAck.checked;
    this.els.aiAckWrap?.classList.toggle('is-invalid', ackError);
    if (this.els.aiAck) {
      if (ackError) this.els.aiAck.setAttribute('aria-invalid', 'true');
      else this.els.aiAck.removeAttribute('aria-invalid');
    }
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      delete el.dataset.tone;
      return;
    }
    el.hidden = false;
    el.dataset.tone = tone;
    el.textContent = message;
  }

  #setGlobalStatus(message, tone = 'info') {
    const el = this.els.globalStatus;
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.dataset.tone = tone;
    el.textContent = message;
  }

  #validateStep(step) {
    if (step === 1 && !this.state.photo) {
      return 'Please upload a full-body photo before continuing.';
    }
    if (step === 2 && !this.state.size) {
      return 'Please choose a size.';
    }
    if (step === 3 && (!this.state.baseShape || !this.state.baseColour)) {
      return 'Please choose a base shape and colour.';
    }
    return '';
  }

  #onNext() {
    const error = this.#validateStep(this.state.step);
    if (error) {
      this.#setGlobalStatus(error, 'error');
      if (this.state.step === 1) this.#setPhotoError(error);
      return;
    }
    this.#setGlobalStatus('');
    this.#setPhotoError('');
    this.#goToStep(Math.min(STEP_COUNT, this.state.step + 1));
  }

  async #onGenerate() {
    if (this.generateInFlight) return;

    const missing = this.#validateStep(1) || this.#validateStep(2) || this.#validateStep(3);
    if (missing) {
      this.#setGlobalStatus(missing, 'error');
      return;
    }

    this.state.status = 'GENERATING';
    this.state.generatedPreviewUrl = null;
    if (this.els.aiAck) this.els.aiAck.checked = false;
    this.#setAtcValidation('');
    this.els.aiDisclaimer?.toggleAttribute('hidden', true);
    this.els.aiAckWrap?.toggleAttribute('hidden', true);
    if (this.els.generatedPreview) {
      this.els.generatedPreview.removeAttribute('src');
      this.els.generatedPreview.hidden = true;
    }
    this.els.previewResult?.toggleAttribute('hidden', true);
    this.els.baseOverlay?.toggleAttribute('hidden', true);
    this.els.generationNotice?.toggleAttribute('hidden', true);
    this.#setGlobalStatus('');
    this.#setPhotoError('');
    this.#startEstimatedProgress();
    this.#updatePreviewStatus();
    this.#updateActions();
    this.#updateReadyPanel();

    this.generateInFlight = (async () => {
      try {
        const result = await generateBobblehead(this.state, this.config);
        if (result.ok && result.previewUrl && /^https:\/\//i.test(result.previewUrl)) {
          await this.#completeGeneration(result);
        } else {
          this.#stopEstimatedProgress();
          this.state.status = 'ERROR';
          this.state.generatedPreviewUrl = null;
          const message =
            result.message ||
            'We could not generate your bobblehead preview right now. Please try again in a moment.';
          if (this.els.generationNotice) {
            this.els.generationNotice.textContent = message;
            this.els.generationNotice.hidden = false;
          }
          this.#setGlobalStatus(message, 'error');
        }
      } catch {
        this.#stopEstimatedProgress();
        this.state.status = 'ERROR';
        this.state.generatedPreviewUrl = null;
        const message = 'Something went wrong while generating your preview. Please try again.';
        if (this.els.generationNotice) {
          this.els.generationNotice.textContent = message;
          this.els.generationNotice.hidden = false;
        }
        this.#setGlobalStatus(message, 'error');
      } finally {
        this.generateInFlight = null;
        this.#updatePreviewStatus();
        this.#updateActions();
        this.#updateReadyPanel();
      }
    })();

    await this.generateInFlight;
  }

  /**
   * @param {{ previewUrl: string, customizationId?: string }} result
   */
  async #completeGeneration(result) {
    this.#setEstimatedProgress(100, 'Your personalised bobblehead preview');
    this.#stopEstimatedProgress();

    this.state.generatedPreviewUrl = result.previewUrl;
    this.state.status = 'READY';
    if (result.customizationId) this.state.customizationId = result.customizationId;

    const img = this.els.generatedPreview;
    if (img) {
      await new Promise((resolve) => {
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
        img.hidden = false;
        img.src = result.previewUrl;
        if (img.complete) done();
      });
    }

    this.els.previewResult?.toggleAttribute('hidden', false);
    this.els.previewPlaceholder?.toggleAttribute('hidden', true);
    this.els.generationProgress?.toggleAttribute('hidden', true);
    this.els.generationNotice?.toggleAttribute('hidden', true);
    this.els.aiDisclaimer?.toggleAttribute('hidden', false);
    this.els.aiAckWrap?.toggleAttribute('hidden', false);
    if (this.els.aiAck) this.els.aiAck.checked = false;
    this.#updateBaseOverlay();
    this.#setGlobalStatus('');
  }

  #startEstimatedProgress() {
    this.#stopEstimatedProgress();
    this.progressStartedAt = Date.now();
    this.estimatedProgress = 0;
    this.#renderEstimatedProgress();
    this.progressTimer = window.setInterval(() => {
      const elapsed = (Date.now() - this.progressStartedAt) / 1000;
      const ratio = Math.min(1, elapsed / GENERATION_ESTIMATE_SECONDS);
      // Ease toward 95% so the bar never looks stuck, but never hits 100% early.
      const next = Math.min(95, Math.round(5 + ratio * 90));
      if (next > this.estimatedProgress) this.estimatedProgress = next;
      this.#renderEstimatedProgress(elapsed > GENERATION_ESTIMATE_SECONDS + 8);
    }, 350);
  }

  #stopEstimatedProgress() {
    if (this.progressTimer != null) {
      window.clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  #setEstimatedProgress(value, stageLabel) {
    this.estimatedProgress = value;
    if (this.els.progressStage && stageLabel) this.els.progressStage.textContent = stageLabel;
    this.#renderEstimatedProgress(false, true);
  }

  #renderEstimatedProgress(takingLonger = false, forceComplete = false) {
    const pct = Math.max(0, Math.min(forceComplete ? 100 : 95, this.estimatedProgress));
    if (this.els.progressFill) this.els.progressFill.style.width = `${pct}%`;
    if (this.els.progressTrack) this.els.progressTrack.setAttribute('aria-valuenow', String(pct));
    if (this.els.progressPercent) {
      this.els.progressPercent.textContent = `Estimated progress: ${pct}%`;
    }

    let stage = GENERATION_STAGES[0].label;
    for (const item of GENERATION_STAGES) {
      if (pct >= item.at) stage = item.label;
    }
    if (takingLonger) {
      stage = 'Taking a little longer than usual — your preview is still being created.';
    }
    if (!forceComplete && this.els.progressStage) this.els.progressStage.textContent = stage;

    if (this.els.progressEta) {
      if (forceComplete || pct >= 100) {
        this.els.progressEta.textContent = 'Estimated time remaining: ready';
      } else if (takingLonger) {
        this.els.progressEta.textContent = 'Estimated time remaining: almost there…';
      } else {
        const elapsed = (Date.now() - this.progressStartedAt) / 1000;
        const remaining = Math.max(3, Math.ceil(GENERATION_ESTIMATE_SECONDS - elapsed));
        this.els.progressEta.textContent = `Estimated time remaining: ~${remaining} seconds`;
      }
    }
  }

  #updateBaseOverlay() {
    const text = this.state.baseText.trim();
    const overlay = this.els.baseOverlay;
    const label = this.els.baseOverlayText;
    if (!overlay || !label) return;

    const ready = this.state.status === 'READY' && this.state.generatedPreviewUrl;
    if (!ready || !text) {
      overlay.hidden = true;
      label.textContent = '';
      return;
    }

    label.textContent = text;
    // No CSS pedestal — colour comes from the AI-generated base. Only pick readable text tone.
    const lightBases = new Set(['white', 'yellow']);
    overlay.dataset.textTone = lightBases.has(this.state.baseColour) ? 'dark' : 'light';
    overlay.hidden = false;
  }

  async #onAddToCart() {
    if (this.cartSubmitInFlight) return;

    if (!this.#canAddToCart()) {
      if (
        this.state.status === 'READY' &&
        this.state.generatedPreviewUrl &&
        this.els.aiAck &&
        !this.els.aiAck.checked
      ) {
        this.#setAtcValidation(
          'Please confirm you understand the AI preview disclaimer before adding to cart.'
        );
        return;
      }
      this.#setGlobalStatus(
        'Add to cart unlocks after a genuine bobblehead preview is generated.',
        'info'
      );
      return;
    }

    const variantId = this.state.variantId;
    if (!variantId) {
      this.#setGlobalStatus(
        'Shopify size variants are not linked yet. Connect the Custom Sports Bobblehead product in theme settings.',
        'error'
      );
      return;
    }

    const previewUrl = this.state.generatedPreviewUrl;
    if (!previewUrl || !/^https:\/\//i.test(previewUrl)) {
      this.#setGlobalStatus(
        'Your generated preview is missing. Please generate your bobblehead again before adding to cart.',
        'error'
      );
      return;
    }

    this.cartSubmitInFlight = true;
    if (this.els.addToCart) {
      this.els.addToCart.disabled = true;
      this.els.addToCart.setAttribute('aria-disabled', 'true');
    }
    this.#setAtcValidation('Adding to cart…', 'info');

    const formData = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            Size: this.#sizeLabel(),
            Subject: this.#subjectLabel(),
            'Base Shape': this.#shapeLabel(),
            'Base Colour': this.#colourLabel(),
            'Base Text': this.state.baseText.trim() || '—',
            '_Customization ID': this.state.customizationId || createTemporaryCustomizationId(),
            '_Original Photo Reference': 'pending-upload',
            // Persistent HTTPS Blob URL for fulfilment (never a data URL).
            '_Generated Preview Reference': previewUrl,
          },
        },
      ],
    };

    try {
      const response = await fetch(cartAddEndpoint(this.config), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'same-origin',
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      const addedItems = Array.isArray(payload?.items)
        ? payload.items
        : payload?.variant_id
          ? [payload]
          : [];
      const shopifyError = Boolean(payload?.status) || Boolean(payload?.message && !addedItems.length);
      const confirmed = response.ok && !shopifyError && addedItems.length > 0;

      if (!confirmed) {
        this.cartSubmitInFlight = false;
        this.#setAtcValidation(
          payload?.description ||
            payload?.message ||
            'We could not add this to your cart. Please check your selections and try again.'
        );
        this.#updateActions();
        return;
      }

      // Confirmed by Shopify — take the customer to the cart page.
      const cartUrl =
        (typeof window.Shopify?.routes?.root === 'string' &&
          `${window.Shopify.routes.root.replace(/\/?$/, '/') }cart`) ||
        '/cart';
      window.location.assign(cartUrl);
    } catch {
      this.cartSubmitInFlight = false;
      this.#setAtcValidation('Network error while adding to cart. Please try again.');
      this.#updateActions();
    }
  }

  #canAddToCart() {
    const ackOk = !this.els.aiAck || this.els.aiAck.checked;
    return Boolean(
      this.state.photo &&
        this.state.size &&
        this.state.baseShape &&
        this.state.baseColour &&
        this.state.status === 'READY' &&
        this.state.generatedPreviewUrl &&
        /^https:\/\//i.test(this.state.generatedPreviewUrl) &&
        this.state.variantId &&
        this.config.hasProduct &&
        ackOk
    );
  }

  #goToStep(step) {
    if (step < 1 || step > STEP_COUNT) return;
    this.state.step = step;
    this.#render();
  }

  #subjectLabel() {
    return this.config.subjects?.find((s) => s.id === this.state.subject)?.label || this.state.subject || 'Anyone';
  }

  #sizeLabel() {
    return this.config.sizes.find((s) => s.id === this.state.size)?.label || this.state.size;
  }

  #shapeLabel() {
    return this.config.baseShapes.find((s) => s.id === this.state.baseShape)?.label || this.state.baseShape;
  }

  #colourLabel() {
    return this.config.baseColours.find((c) => c.id === this.state.baseColour)?.label || this.state.baseColour;
  }

  #updateCharCount() {
    const max = this.config.maxBaseTextLength || 15;
    const len = this.state.baseText.length;
    if (this.els.baseTextCount) this.els.baseTextCount.textContent = `${len} / ${max}`;
  }

  #updateSummary() {
    const currency = this.config.currency || 'GBP';
    if (this.els.summarySize) this.els.summarySize.textContent = this.#sizeLabel();
    if (this.els.summaryShape) this.els.summaryShape.textContent = this.#shapeLabel();
    if (this.els.summaryColour) this.els.summaryColour.textContent = this.#colourLabel();
    if (this.els.summaryText) {
      this.els.summaryText.textContent = this.state.baseText.trim() || 'None';
    }
    if (this.els.summaryPrice) this.els.summaryPrice.textContent = formatMoney(this.state.price, currency);
    if (this.els.summaryPhoto && this.state.photoPreviewUrl) {
      this.els.summaryPhoto.src = this.state.photoPreviewUrl;
      this.els.summaryPhoto.hidden = false;
    }
    this.#updateReadyPanel();
  }

  #updateReadyPanel() {
    const currency = this.config.currency || 'GBP';
    const isReady = this.state.status === 'READY' && this.state.generatedPreviewUrl;
    this.els.readyPanel?.toggleAttribute('hidden', !isReady);
    this.els.reviewGenerate?.toggleAttribute('hidden', isReady);

    if (this.els.readySize) this.els.readySize.textContent = this.#sizeLabel();
    if (this.els.readyShape) this.els.readyShape.textContent = this.#shapeLabel();
    if (this.els.readyColour) this.els.readyColour.textContent = this.#colourLabel();
    if (this.els.readyText) this.els.readyText.textContent = this.state.baseText.trim() || 'None';
    if (this.els.readyPrice) this.els.readyPrice.textContent = formatMoney(this.state.price, currency);
  }

  #updatePreviewChrome() {
    const currency = this.config.currency || 'GBP';
    if (this.els.previewSizeLabel) {
      this.els.previewSizeLabel.textContent = `${this.#sizeLabel()} configuration`;
    }
    if (this.els.previewPrice) {
      this.els.previewPrice.textContent = formatMoney(this.state.price, currency);
    }
    if (this.els.atcLabel) {
      this.els.atcLabel.textContent = `Add to cart — ${formatMoney(this.state.price, currency)}`;
    }
    this.#updateBaseOverlay();
  }

  #updatePreviewStatus() {
    const stage = this.els.previewStage;
    const label = this.els.previewStateLabel;
    if (!stage) return;

    stage.dataset.status = this.state.status;
    const isReady = this.state.status === 'READY' && this.state.generatedPreviewUrl;
    const isGenerating = this.state.status === 'GENERATING';

    this.els.generationProgress?.toggleAttribute('hidden', !isGenerating);
    this.els.previewPlaceholder?.toggleAttribute('hidden', Boolean(isReady || isGenerating));
    this.els.previewResult?.toggleAttribute('hidden', !isReady);
    this.els.aiDisclaimer?.toggleAttribute('hidden', !isReady);
    this.els.aiAckWrap?.toggleAttribute('hidden', !isReady);

    if (label) {
      const labels = {
        EMPTY: 'Your personalised preview will appear here after you generate it.',
        UPLOADING: 'Preparing your photo…',
        PROCESSING: 'Processing your configuration…',
        GENERATING: 'Creating your bobblehead…',
        READY: 'Your personalised bobblehead preview',
        ERROR: 'Generation failed — check the message below and try again.',
      };
      label.textContent = labels[this.state.status] || labels.EMPTY;
    }

    if (!isReady && this.els.generatedPreview) {
      this.els.generatedPreview.hidden = true;
    }
    if (!isReady) {
      this.els.baseOverlay?.toggleAttribute('hidden', true);
      if (this.els.aiAck) this.els.aiAck.checked = false;
      this.#setAtcValidation('');
    } else {
      this.#updateBaseOverlay();
    }
  }

  #updateActions() {
    const step = this.state.step;
    const isFirst = step === 1;
    const isLast = step === STEP_COUNT;
    const isReady = this.state.status === 'READY' && this.state.generatedPreviewUrl;

    this.els.back?.toggleAttribute('hidden', isFirst);
    this.els.next?.toggleAttribute('hidden', isLast);
    this.els.generate?.toggleAttribute('hidden', !isLast || isReady);
    this.els.addToCart?.toggleAttribute('hidden', !isLast || !isReady);
    this.els.edit?.toggleAttribute('hidden', !isLast || !isReady);

    if (this.els.addToCart) {
      this.els.addToCart.disabled = this.cartSubmitInFlight;
      this.els.addToCart.setAttribute('aria-disabled', String(this.cartSubmitInFlight));
    }

    if (this.els.generate) {
      const generating = this.state.status === 'GENERATING';
      this.els.generate.disabled = generating;
      this.els.generate.textContent = generating
        ? 'Generating your bobblehead…'
        : 'Generate my bobblehead';
    }
  }

  #updateProgress() {
    this.els.progressItems.forEach((item) => {
      const step = Number(item.getAttribute('data-progress-step'));
      item.classList.toggle('is-current', step === this.state.step);
      item.classList.toggle('is-complete', step < this.state.step);
      if (step === this.state.step) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  #updatePanels() {
    this.els.panels.forEach((panel) => {
      const step = Number(panel.getAttribute('data-step-panel'));
      const active = step === this.state.step;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('hidden', !active);
    });
  }

  #render() {
    this.#updateProgress();
    this.#updatePanels();
    this.#updatePreviewChrome();
    this.#updatePreviewStatus();
    this.#updateSummary();
    this.#updateCharCount();
    this.#updateActions();
  }
}

function readConfig(root) {
  const sectionId = root.getAttribute('data-section-id');
  const dataEl = document.getElementById(`BobbleheadConfigurator-Data-${sectionId}`);
  if (!dataEl) throw new Error('Configurator data missing');
  return JSON.parse(dataEl.textContent || '{}');
}

function init() {
  document.querySelectorAll('[data-bobblehead-configurator]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.initialized === 'true') return;
    try {
      const config = readConfig(root);
      new BobbleheadConfigurator(root, config);
      root.dataset.initialized = 'true';
    } catch (error) {
      console.error('[MakeMyBobblehead] Failed to initialise configurator', error);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target?.querySelector?.('[data-bobblehead-configurator]') || event.target;
  if (section?.matches?.('[data-bobblehead-configurator]')) {
    section.dataset.initialized = 'false';
    init();
  }
});
